/**
 * TopBarSearchResults Component
 *
 * Dropdown component that displays search results below the top bar search input.
 * Shows categorized results for clients, users, time entries, invoices, and activities.
 */

import React from 'react';
import { FiUsers, FiUser, FiClock, FiFileText, FiActivity, FiBook, FiLoader, FiSearch } from 'react-icons/fi';
import '../styles/TopBarSearchResults.css';

const TopBarSearchResults = ({
  searchQuery,
  searchResults,
  isLoading,
  recentSearches,
  searchType,
  onSearchTypeChange,
  onResultClick,
  isOpen
}) => {
  // Get icon for result type
  const getResultIcon = (type) => {
    const iconMap = {
      client: FiUsers,
      user: FiUser,
      timeEntry: FiClock,
      invoice: FiFileText,
      activity: FiActivity,
      guide: FiBook
    };
    return iconMap[type] || FiActivity;
  };

  // Get result metadata text (backend supplies a display-ready subtitle)
  const getResultMeta = (result) => {
    return result?.subtitle || result?.email || result?.client_name || '';
  };

  // Right-aligned detail: the number that identifies this row at a glance
  const getResultDetail = (result) => {
    if (result?.type === 'invoice' && result.meta?.total != null) {
      const amount = Number(result.meta.total);
      if (!Number.isNaN(amount)) {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
    if (result?.type === 'timeEntry' && result.meta?.hours != null) {
      return `${result.meta.hours}h`;
    }
    return null;
  };

  // Count total results
  const getTotalResults = () => {
    return Object.values(searchResults).reduce((total, results) => total + (results?.length || 0), 0);
  };

  const totalResults = getTotalResults();

  // Category filter buttons
  const categoryFilters = [
    { key: 'all', label: 'All', icon: FiSearch },
    { key: 'clients', label: 'Clients', icon: FiUsers },
    { key: 'users', label: 'Users', icon: FiUser },
    { key: 'timeEntries', label: 'Time', icon: FiClock },
    { key: 'invoices', label: 'Invoices', icon: FiFileText },
    { key: 'activities', label: 'Activities', icon: FiActivity },
    { key: 'guide', label: 'Guide', icon: FiBook }
  ];

  // Format date for recent searches
  const formatRecentDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Shared row renderer so every category presents results identically
  const renderResultItem = (result, { key, detailOverride } = {}) => {
    const type = result?.type || 'activity';
    const Icon = getResultIcon(type);
    const meta = getResultMeta(result);
    const detail = detailOverride !== undefined ? detailOverride : getResultDetail(result);

    return (
      <div
        key={key ?? result.id}
        className={`search-result-item result-type--${type}`}
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent blur event
          onResultClick(result);
        }}
      >
        <div className="result-icon">
          <Icon size={16} />
        </div>
        <div className="result-content">
          <div className="result-title">{result.title}</div>
          {meta && <div className="result-meta">{meta}</div>}
        </div>
        {detail && <div className="result-detail">{detail}</div>}
      </div>
    );
  };

  // Show recent searches when empty
  if (!searchQuery && !isLoading) {
    const recentToShow = recentSearches.slice(0, 5);

    return (
      <div className={`topbar-search-results ${isOpen ? 'open' : ''}`}>
        {recentToShow.length > 0 ? (
          <>
            <div className="search-results-header">
              <span className="results-count">Recent Searches</span>
            </div>
            <div className="search-results-list">
              {recentToShow.map((search) =>
                renderResultItem(
                  {
                    ...(search.result || {}),
                    type: search.result?.type || search.type,
                    title: search.result?.title || search.query
                  },
                  {
                    key: search.id,
                    detailOverride: (
                      <span className="result-timestamp">{formatRecentDate(search.timestamp)}</span>
                    )
                  }
                )
              )}
            </div>
          </>
        ) : (
          <div className="search-results-empty">
            <p>Start typing to search</p>
            <span>Search for clients, users, time entries, invoices, or activities</span>
          </div>
        )}
      </div>
    );
  }

  // Don't return early for loading - show results while loading to prevent flash
  // Loading indicator will be shown in the header instead

  // Filter results based on searchType
  const shouldShowCategory = (category) => {
    return searchType === 'all' || searchType === category;
  };

  // Calculate filtered results count based on selected tab
  const getFilteredResultsCount = () => {
    if (searchType === 'all') return totalResults;
    const categoryResults = searchResults[searchType];
    return categoryResults?.length || 0;
  };

  const filteredResultsCount = getFilteredResultsCount();

  // Category sections rendered from one definition instead of six copies
  const categorySections = [
    { key: 'clients', label: 'Clients', icon: FiUsers },
    { key: 'users', label: 'Users', icon: FiUser },
    { key: 'timeEntries', label: 'Time Entries', icon: FiClock },
    { key: 'invoices', label: 'Invoices', icon: FiFileText },
    { key: 'activities', label: 'Activities', icon: FiActivity },
    { key: 'guide', label: 'User Guide', icon: FiBook }
  ];

  // Render results with filters (always show tabs when there's a search query)
  return (
    <div className={`topbar-search-results ${isOpen ? 'open' : ''}`}>
      {/* Category Filter Buttons - Always show when searching */}
      <div className="search-category-filters">
        {categoryFilters.map((filter) => {
          const Icon = filter.icon;
          const isActive = searchType === filter.key;
          // Show count badge for each category
          const categoryCount = filter.key === 'all'
            ? totalResults
            : (searchResults[filter.key]?.length || 0);
          return (
            <button
              key={filter.key}
              className={`category-filter-btn ${isActive ? 'active' : ''} ${categoryCount === 0 && filter.key !== 'all' ? 'empty' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur event
                onSearchTypeChange(filter.key);
              }}
            >
              <Icon size={14} />
              <span>{filter.label}</span>
              {categoryCount > 0 && <span className="filter-count">{categoryCount}</span>}
            </button>
          );
        })}
      </div>

      <div className="search-results-header">
        <span className="results-count">
          {isLoading ? (
            <>
              <FiLoader className="loading-spinner-inline" size={12} />
              Searching...
            </>
          ) : (
            `${filteredResultsCount} result${filteredResultsCount !== 1 ? 's' : ''}`
          )}
        </span>
      </div>

      {/* Show empty state for filtered view - only when not loading */}
      {!isLoading && filteredResultsCount === 0 && (
        <div className="search-results-empty">
          <p>No {searchType === 'all' ? '' : searchType + ' '}results found for "{searchQuery}"</p>
          <span>{searchType !== 'all' ? 'Try selecting a different category' : 'Try a different search term'}</span>
        </div>
      )}

      <div className="search-results-list">
        {categorySections.map(({ key, label, icon: SectionIcon }) => {
          if (!shouldShowCategory(key) || !searchResults[key]?.length) return null;
          return (
            <div key={key} className="search-results-category">
              <div className="category-header">
                <SectionIcon size={14} />
                <span>{label} ({searchResults[key].length})</span>
              </div>
              {searchResults[key].map((result) => renderResultItem(result))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TopBarSearchResults;
